import { useId, useState } from 'react';
import { Badge, Box, Button, Link } from '@amzn/awsui-components-react';
import type {
  AccountPipelineGroup,
  AccountPipelineOpportunity,
  KpiBucket,
} from '@amzn/ivthub-client';
import { ABSENT_MARK, formatArr } from './scale';
import { normalizeSfdcStage, SFDC_STAGE_COLOR } from '../../../utils/sfdcStage';

/**
 * One account inside 我的灯塔客户 / 圈外归仓: its header figures, and its tagged and untagged
 * opportunities beneath.
 *
 * The tag side is WHICH LIST a row is in, never re-derived: an untagged row whose every suggestion
 * was dismissed still has them. Not a `Table`, because the comp has no column header row and
 * Cloudscape always renders one.
 */

/** `20 opps, $780K` — the shape the design writes an account's buckets in. */
const bucketText = (b: KpiBucket | undefined) => `${b?.oppCount ?? 0} opps, ${formatArr(b?.arr)}`;

/** Short form within the current year; full ISO otherwise, since a bare `12/08` reads as this year. */
function shortDate(iso: string, todayMs: number): string {
  const [year, month, day] = iso.split('-');
  return Number(year) === new Date(todayMs).getUTCFullYear() ? `${Number(month)}/${day}` : iso;
}

/**
 * Whole UTC days, against a clock taken once per render — per-row clocks can straddle midnight and
 * date two identical rows a day apart.
 */
function daysAgo(iso: string, todayMs: number): number | null {
  const then = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(then)) return null;
  return Math.floor((todayMs - then) / 86_400_000);
}

/**
 * The row's trailing date, the same rule on both sides: the latest activity, else the creation date
 * under the 建单 label. The activity carries no subject and needs none — when it happened is what
 * the column asks.
 */
function DateCell({ opp, todayMs }: { opp: AccountPipelineOpportunity; todayMs: number }) {
  const activity = opp.lastActivityDate;
  const date = activity ?? opp.createdDate;
  if (date === undefined) return <Box color="text-status-inactive">{ABSENT_MARK}</Box>;
  const days = daysAgo(date, todayMs);
  const age = days === null ? '' : activity !== undefined ? ` · ${days} 天前` : ` · ${days} 天`;
  return (
    <>
      {shortDate(date, todayMs)}
      {activity === undefined && ' 建单'}
      {age}
    </>
  );
}

/**
 * The transition renders on `prevStage` ALONE: `stageChangedDate` is set upstream even for a row
 * that never moved, so testing the date would badge every row as having moved.
 *
 * The pill's fill is the Figma color-table hex for the TERMINAL stage (DAT-9259 item 2) — the
 * current stage, or the destination of a transition. Text is white; an unknown stage falls to
 * the Others color so a response using a stage this map does not know still reads as a pill.
 * A Cloudscape `Badge` carries hard-coded colors from `color=`, so we render our own span to
 * use the stage hexes directly.
 */
function StageCell({ opp }: { opp: AccountPipelineOpportunity }) {
  const stage = opp.sfdcStage ?? ABSENT_MARK;
  const moved = opp.prevStage !== undefined && opp.prevStage !== '';
  const terminalKey = normalizeSfdcStage(stage);
  const bg = SFDC_STAGE_COLOR[terminalKey] ?? SFDC_STAGE_COLOR['Others'];
  return (
    <span className="ivt-dashboard__stage-pill" style={{ backgroundColor: bg }}>
      {moved ? `${opp.prevStage} → ${stage}` : stage}
    </span>
  );
}

/**
 * The server's floor when the request omits one. The server selects OPPORTUNITIES by it; the chips
 * beneath a selected opportunity come back whatever their score, and the product rule is that none
 * below the floor is shown. So the page needs the number too — keep it equal to the service's.
 */
export const DEFAULT_MIN_SCORE = 3;

/**
 * The comp colours a recommendation by its SCORE, not by the row's stage: one Committed row carries
 * an olive 3.0 beside a green 1.0. Figma names each colour as a Cloudscape chart-palette token (see
 * `.ivt-dashboard__asset` in DashboardPage.css). Nothing below the floor renders, so the comp's
 * green 1 has no use here.
 */
const SCORE_CLASS: Record<number, string> = {
  5: 'is-score-5',
  4: 'is-score-4',
  3: 'is-score-3',
};

/**
 * The recommendations a row shows: at or above the floor. An unscored one is not at or above
 * anything. Ignore offers exactly these, so the dialog never lists a chip the row hides.
 */
export function visibleSuggestions(opp: AccountPipelineOpportunity, minScore: number) {
  return (opp.suggestions ?? []).flatMap((s) =>
    s.assetId !== undefined && s.relevanceScore != null && s.relevanceScore >= minScore
      ? [{ ...s, assetId: s.assetId, relevanceScore: s.relevanceScore }]
      : [],
  );
}

/** Whether a pair reads as dismissed: a write this page made wins over what the list said. */
export type DismissedOf = (opptyId: string, assetId: string, onWire: boolean) => boolean;

/**
 * One asset on a row. A tagged one is the name alone — a tag is not a recommendation, so it has no
 * score. A recommended one leads with its score; the decimal is display only, `relevanceScore` is
 * an integer on the wire. A dismissed one is struck through and greyed rather than hidden — the
 * service still reports it.
 */
function AssetBox({
  name,
  score,
  dismissed = false,
}: {
  name: string;
  score?: number;
  dismissed?: boolean;
}) {
  const tone = dismissed || score === undefined ? '' : (SCORE_CLASS[score] ?? '');
  return (
    <span className={`ivt-dashboard__asset ${tone}`}>
      {score !== undefined && (
        <span className="ivt-dashboard__asset-score">{score.toFixed(1)}</span>
      )}
      <span className="ivt-dashboard__asset-name">{dismissed ? <s>{name}</s> : name}</span>
    </span>
  );
}

/** One compact line: name · ARR · stage · suggestions · actions · date. */
function OppRow({
  opp,
  untagged,
  minScore,
  dismissedOf,
  onIgnore,
  onClaim,
  todayMs,
}: {
  opp: AccountPipelineOpportunity;
  untagged: boolean;
  minScore: number;
  dismissedOf: DismissedOf;
  onIgnore?: (opp: AccountPipelineOpportunity) => void;
  onClaim?: (opp: AccountPipelineOpportunity) => void;
  todayMs: number;
}) {
  const suggestions = untagged ? visibleSuggestions(opp, minScore) : [];
  return (
    // Not a Cloudscape `Grid`: its twelve fractional columns cannot hold `Ignore` at a fixed
    // distance from the right edge, which is what lets the header's Delete line up with it.
    <div className="ivt-dashboard__grid ivt-dashboard__opp-row">
      {/* `oppName` is optional; `opptyId` is the one required member, so it is the fallback
          rather than a placeholder — the row stays identifiable. */}
      <Box>{opp.oppName ?? opp.opptyId ?? ABSENT_MARK}</Box>
      {/* A dash, not `$0`: `opportunity_cache.arr` has no NOT NULL, so absence means unrecorded
          and `$0` would assert a figure nobody captured. The account header's total is different
          — the server summed that, so ITS zero is an answer. This is the divergence the group's
          own numbers contain: such a row is counted and not added. */}
      <Box fontWeight="bold">
        {opp.arr == null ? (
          <Box color="text-status-inactive">{ABSENT_MARK}</Box>
        ) : (
          formatArr(opp.arr)
        )}
      </Box>
      {/* Not a `SpaceBetween`: its item wrappers size to their content, so an asset box could
          never shrink to the column and a long name ran into the buttons beside it. */}
      <div className="ivt-dashboard__row-tags">
        <StageCell opp={opp} />
        {/* The contract has no activity SUBJECT and needs none: what the page asks is "did IVT
            touch this", and `hasIvtActivity` answers it. On an untagged row that is the "IVT
            worked it with no tag to show for it" case. The comp has no slot for it. */}
        {opp.hasIvtActivity === true && <Badge color="blue">IVT 活动</Badge>}
        {untagged
          ? suggestions.map((s) => (
              <AssetBox
                key={s.assetId}
                name={s.assetName ?? s.assetId}
                score={s.relevanceScore}
                dismissed={dismissedOf(opp.opptyId ?? '', s.assetId, s.isDismissed === true)}
              />
            ))
          : (opp.taggedAssets ?? []).map((a) => (
              <AssetBox key={a.assetId} name={a.assetName ?? a.assetId ?? ABSENT_MARK} />
            ))}
      </div>
      {/* The same rule as Ignore (product): a row with a recommendation it shows, for a user who
          may write. The recommendations are also what Create Motion pre-ticks. */}
      <div>
        {onClaim && suggestions.length > 0 && (
          <Button variant="inline-link" iconName="add-plus" onClick={() => onClaim(opp)}>
            Motion
          </Button>
        )}
      </div>
      {/* Only where there is something to ignore — a row with recommendations — and only for a
          user who may write; the cell stays so the date keeps its column. Not row-level: it
          opens the row's recommendations and writes per (opportunity, asset). */}
      <div>
        {onIgnore && suggestions.length > 0 && (
          <Button variant="inline-link" iconName="face-sad" onClick={() => onIgnore(opp)}>
            Ignore
          </Button>
        )}
      </div>
      <Box color="text-body-secondary" textAlign="right">
        <DateCell opp={opp} todayMs={todayMs} />
      </Box>
    </div>
  );
}

/** Rows shown before `View All`. The rest are already in hand — see the expander below. */
const COLLAPSED_ROWS = 2;

function OppSection({
  title,
  bucket,
  items,
  untagged,
  minScore,
  dismissedOf,
  onIgnore,
  onClaim,
  todayMs,
}: {
  title: string;
  bucket: KpiBucket | undefined;
  items: AccountPipelineOpportunity[];
  untagged: boolean;
  minScore: number;
  dismissedOf: DismissedOf;
  onIgnore?: (opp: AccountPipelineOpportunity) => void;
  onClaim?: (opp: AccountPipelineOpportunity) => void;
  todayMs: number;
}) {
  /**
   * Purely local. The section already holds every row the account has — `oppCount` equals
   * `items.length` — so expanding fetches nothing and cannot be a partial view.
   */
  const [showAll, setShowAll] = useState(false);
  /**
   * An untagged row whose every recommendation the user has ignored has nothing left to act on,
   * so it goes last and out of the collapsed view — reachable only under View All (product
   * rule). Still counted in the header: those figures are the server's, and it still returns
   * the row.
   */
  const ignored = (o: AccountPipelineOpportunity) => {
    if (!untagged) return false;
    const shownHere = visibleSuggestions(o, minScore);
    return (
      shownHere.length > 0 &&
      shownHere.every((sg) => dismissedOf(o.opptyId ?? '', sg.assetId, sg.isDismissed === true))
    );
  };
  const active = items.filter((o) => !ignored(o));
  const ordered = [...active, ...items.filter(ignored)];
  const shown = showAll ? ordered : active.slice(0, COLLAPSED_ROWS);
  const hiddenIgnored = items.length - active.length;

  return (
    // Labelled so the two sides stay tellable apart without a table's row grouping.
    <div role="group" aria-label={title}>
      <div className="ivt-dashboard__section-head">
        <Box fontWeight="bold">
          {title}{' '}
          <Box variant="span" fontWeight="normal">
            ({bucketText(bucket)})
          </Box>
        </Box>
        {ordered.length > Math.min(active.length, COLLAPSED_ROWS) && (
          // `href` is what gets the comp's look: without one Cloudscape draws the link as a button,
          // bold and never underlined, whatever the variant. `onFollow` keeps it local.
          <Link
            variant="primary"
            href="#"
            onFollow={(e) => {
              e.preventDefault();
              setShowAll((v) => !v);
            }}
          >
            {showAll ? 'Show Less' : 'View All'}
          </Link>
        )}
      </div>
      {items.length === 0 ? (
        <div className="ivt-dashboard__section-empty">
          <Box color="text-body-secondary">无</Box>
        </div>
      ) : shown.length === 0 ? (
        // Every row is an ignored one and the section is collapsed: say so rather than look empty.
        <div className="ivt-dashboard__section-empty">
          <Box color="text-body-secondary">{hiddenIgnored} 个机会的推荐已全部忽略</Box>
        </div>
      ) : (
        shown.map((o) => (
          <OppRow
            key={o.opptyId}
            opp={o}
            untagged={untagged}
            minScore={minScore}
            dismissedOf={dismissedOf}
            onIgnore={onIgnore}
            onClaim={onClaim}
            todayMs={todayMs}
          />
        ))
      )}
    </div>
  );
}

const wireDismissed: DismissedOf = (_opptyId, _assetId, onWire) => onWire;

/** Why Add is disabled while a batch runs — its hover tooltip (see AccountPipelineList). */
export const WRITES_BUSY_REASON = '正在添加灯塔客户，完成后可继续操作';

export interface AccountPipelineItemProps {
  group: AccountPipelineGroup;
  /** Account View collapses this to its header row; Pipeline View shows the deals. */
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  /** The relevance floor the list was fetched at; `null` is the server's default. */
  minScore?: number | null;
  /** Absent: no Ignore at all — the user may not write, or nothing is wired to write with. */
  onIgnore?: (opp: AccountPipelineOpportunity) => void;
  /** `+ Motion`. Absent on the same terms as `onIgnore`. */
  onClaim?: (opp: AccountPipelineOpportunity) => void;
  /** Defaults to what the list said. */
  dismissedOf?: DismissedOf;
  /** Taken once per paint by the list, so every row on screen is dated against one instant. */
  todayMs: number;
  /**
   * 🗑 Delete — remove this account from the lighthouse list (DAT-9086). The button renders ONLY
   * when this is passed: the list passes it on the lighthouse block, for a caller who may write
   * the flag. So a viewer sees none, and neither does 圈外归仓, whose accounts are not
   * lighthouses to begin with. The grid cell stays either way, keeping the columns aligned.
   */
  onDelete?: () => void;
  /** Shown but disabled — while a batch of adds runs (see AccountPipelineList). */
  deleteDisabled?: boolean;
}

export function AccountPipelineItem({
  group,
  expanded,
  onExpandedChange,
  minScore = null,
  onIgnore,
  onClaim,
  dismissedOf = wireDismissed,
  todayMs,
  onDelete,
  deleteDisabled = false,
}: AccountPipelineItemProps) {
  const account = group.account;
  const floor = minScore ?? DEFAULT_MIN_SCORE;
  const bodyId = useId();
  // `accountName` may be absent — a flagged account can have no `account_cache` row, which this
  // page exists to surface.
  const name = account?.accountName ?? account?.accountId ?? ABSENT_MARK;
  return (
    /*
     * Not an `ExpandableSection`. The comp's card — a 4px corner, a grey header band — is styling
     * Cloudscape does not expose on it, and reaching into its hashed internals would break silently
     * on an upgrade. Owning the header also puts it on the rows' grid, so Delete sits in the Ignore
     * column by construction rather than by a measured width. The disclosure semantics it gave for
     * free are rebuilt on a Cloudscape icon button: `aria-expanded`, `aria-controls`, and the
     * account name as its label.
     */
    <div className="ivt-dashboard__item">
      <div className={`ivt-dashboard__grid ivt-dashboard__item-head${expanded ? ' is-expanded' : ''}`}>
        <div className="ivt-dashboard__item-title">
          <span className="ivt-dashboard__item-caret">
            <Button
              variant="icon"
              iconName={expanded ? 'caret-down-filled' : 'caret-right-filled'}
              ariaLabel={name}
              ariaExpanded={expanded}
              ariaControls={bodyId}
              onClick={() => onExpandedChange(!expanded)}
            />
          </span>
          <Box variant="h3" padding="n">
            {name}
          </Box>
          {/* Outside the heading, and so outside the button's name too: body text in the comp. */}
          <Box variant="span" color="text-body-secondary">
            (Tagged: {bucketText(account?.tagged)}, Untagged: {bucketText(account?.untagged)})
          </Box>
        </div>
        {/* In the Ignore column. Cloudscape has no trash-can icon, so `remove` stands in. */}
        <div>
          {onDelete && (
            <Button
              variant="inline-link"
              iconName="remove"
              ariaLabel={`从灯塔客户中删除 ${name}`}
              onClick={onDelete}
              // No `disabledReason`: Cloudscape ignores it on `inline-link`, which keeps the plain
              // disabled attribute and shows no tooltip. The reason is on Add, in the block header.
              disabled={deleteDisabled}
            >
              Delete
            </Button>
          )}
        </div>
        <div className="ivt-dashboard__item-badge">
          {account?.isBigBet === true && <span className="ivt-dashboard__bigbet-badge">BIG BET</span>}
        </div>
      </div>
      {/* Kept mounted while collapsed, so `aria-controls` always points at something. An all-zero
          account renders in full, both sections empty: "lighthouse customer with no pipeline" is
          the answer, not an absence to hide. */}
      <div id={bodyId} hidden={!expanded}>
        <OppSection
          title="TAGGED PIPELINE"
          bucket={account?.tagged}
          items={group.tagged ?? []}
          untagged={false}
          minScore={floor}
          dismissedOf={dismissedOf}
          onIgnore={onIgnore}
          onClaim={onClaim}
          todayMs={todayMs}
        />
        <OppSection
          title="UNTAGGED PIPELINES"
          bucket={account?.untagged}
          items={group.untagged ?? []}
          untagged
          minScore={floor}
          dismissedOf={dismissedOf}
          onIgnore={onIgnore}
          onClaim={onClaim}
          todayMs={todayMs}
        />
      </div>
    </div>
  );
}
